// Two shapes side by side (Year 8, 2 Nov 2026). A `shape_pair` is two
// triangles, or two shapes with 4 straight sides, the second one drawn
// turned or flipped when the question wants it. Each shape can carry letters
// at its corners, small marks across its sides (1 to 3), arcs in its corners
// (1 to 3), right-angle squares, and labels on its sides and in its corners:
// a length ("7 cm"), an angle size ("50°"), a letter, an expression
// ("x + 4") or "?" for the part to find.
//
// The screen reader reads the picture mark by mark: "Side AB has 1 small
// mark and is labelled 7 cm." It never says "matching", "equal", "the same",
// "congruent", "similar" or the name of a test (wording approved by the
// owner, 2 Nov 2026).
//
// Each shape follows its own numbers where it can: lengths and angle sizes
// written on it are drawn true, and parts with the same number of marks or
// arcs are drawn the same size. The second shape can be a copy of the first
// (turned, flipped, larger or smaller), with its corners matched to the
// first shape's corners. Where the numbers cannot all be drawn true (one
// shape much larger than the other, say), the picture is labelled "not
// drawn to scale". Numbers that badly contradict the drawing, or marks that
// cannot all hold, are refused.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts). The label rules, the fitting and the label
// placing here are shared with practice-quad-diagonals-data.ts.

// ---------------------------------------------------------------------------
// Labels, shared with practice-quad-diagonals-data.ts.

const LENGTH = /^(\d+(?:\.\d+)?) ?(mm|cm|m|km)?$/;
const LETTER = /^[a-zA-Z]$/;
export const CORNER_LETTER = /^[A-Z]$/;
const DEGREES = /^(\d{1,3}(?:\.\d+)?)°$/;
// "2x + 3", "4x − 5", "3(x + 1)": numbers, single small letters and signs.
const EXPRESSION = /^[0-9a-z+−\-×()./ ]+$/;
// An angle written as an expression, such as "(2x + 10)°".
const ANGLE_EXPRESSION = /^\((.+)\)°$/;
// The longest label: room for "(2x + 10)°" and "4x − 5 cm".
export const MAX_LABEL = 12;
// The most marks or arcs on one part.
export const MAX_MARKS = 3;

function isExpression(text: string): boolean {
  return (
    EXPRESSION.test(text) &&
    /[a-z]/.test(text) &&
    !/[a-z]{2}/.test(text) &&
    /[0-9+−\-×(]/.test(text)
  );
}

// A length, a letter, an expression, "?" or "" for a bare part.
export function isPartLabel(text: string): boolean {
  if (text.length > MAX_LABEL) return false;
  return (
    text === '' ||
    text === '?' ||
    LETTER.test(text) ||
    LENGTH.test(text) ||
    isExpression(text)
  );
}

export function degreesOf(text: string): number | null {
  const size = DEGREES.exec(text);
  return size ? Number(size[1]) : null;
}

// An angle size, a letter, an expression in degrees, "?" or "".
export function isAngleLabel(text: string): boolean {
  if (text.length > MAX_LABEL) return false;
  if (text === '' || text === '?' || LETTER.test(text)) return true;
  const inner = ANGLE_EXPRESSION.exec(text);
  if (inner) return isExpression(inner[1] ?? '');
  const degrees = degreesOf(text);
  return degrees !== null && degrees > 0 && degrees < 180;
}

// A length written on a part, with its unit ("" for none), or null.
export function lengthOf(text: string): { size: number; unit: string } | null {
  const length = LENGTH.exec(text);
  if (!length) return null;
  const size = Number(length[1]);
  return size > 0 ? { size, unit: length[2] ?? '' } : null;
}

function spokenExpression(text: string): string {
  return text
    .replace(/\s*\+\s*/g, ' plus ')
    .replace(/\s*[−-]\s*/g, ' minus ')
    .replace(/\s*×\s*/g, ' times ')
    .replace(/\s*\/\s*/g, ' over ')
    .replace(/\s+/g, ' ')
    .trim();
}

// What the screen reader says about a label: "is labelled 7 cm".
export function labelWords(text: string): string {
  if (text === '?') return 'is unknown and marked with a question mark';
  if (LETTER.test(text)) return `is labelled the letter ${text}`;
  const degrees = degreesOf(text);
  if (degrees !== null)
    return `is labelled ${String(degrees)} ${degrees === 1 ? 'degree' : 'degrees'}`;
  const inner = ANGLE_EXPRESSION.exec(text);
  if (inner) return `is labelled ${spokenExpression(inner[1] ?? '')} degrees`;
  if (LENGTH.test(text)) return `is labelled ${text}`;
  return `is labelled ${spokenExpression(text)}`;
}

// The label as it is said inside a list ("7 cm", "50 degrees").
export function listWords(text: string): string {
  if (text === '?') return 'an unknown marked with a question mark';
  return labelWords(text).replace(/^is labelled /, '');
}

export function marksWords(count: number, thing: string): string {
  return `${String(count)} ${thing}${count === 1 ? '' : 's'}`;
}

// The labels in order. Neighbours with no label are counted together.
export function spokenList(labels: string[], thing: string): string {
  const parts: string[] = [];
  let bare = 0;
  const flush = () => {
    if (bare === 1) parts.push(`a ${thing} with no label`);
    if (bare > 1) parts.push(`${String(bare)} ${thing}s with no label`);
    bare = 0;
  };
  for (const label of labels) {
    if (label === '') {
      bare += 1;
      continue;
    }
    flush();
    parts.push(listWords(label));
  }
  flush();
  if (parts.length < 2) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] ?? ''}`;
}

export function joinNames(names: string[]): string {
  if (names.length < 2) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

export function readLabelList(
  value: unknown,
  count: number,
  allowed: (text: string) => boolean,
): string[] | null {
  const bare = Array<string>(count).fill('');
  if (value === undefined) return bare;
  if (!Array.isArray(value)) return null;
  if (value.length === 0) return bare;
  if (value.length !== count) return null;
  const labels: string[] = [];
  for (const entry of value as unknown[]) {
    if (typeof entry !== 'string') return null;
    const text = entry.trim();
    if (!allowed(text)) return null;
    labels.push(text);
  }
  return labels;
}

export function readCountList(value: unknown, count: number): number[] | null {
  const none = Array<number>(count).fill(0);
  if (value === undefined) return none;
  if (!Array.isArray(value)) return null;
  if (value.length === 0) return none;
  if (value.length !== count) return null;
  const counts: number[] = [];
  for (const entry of value as unknown[]) {
    if (
      typeof entry !== 'number' ||
      !Number.isInteger(entry) ||
      entry < 0 ||
      entry > MAX_MARKS
    )
      return null;
    counts.push(entry);
  }
  return counts;
}

function readFlagList(value: unknown, count: number): boolean[] | null {
  const none = Array<boolean>(count).fill(false);
  if (value === undefined) return none;
  if (!Array.isArray(value)) return null;
  if (value.length === 0) return none;
  if (value.length !== count) return null;
  const flags: boolean[] = [];
  for (const entry of value as unknown[]) {
    if (typeof entry !== 'boolean') return null;
    flags.push(entry);
  }
  return flags;
}

// ---------------------------------------------------------------------------
// Geometry, shared with practice-quad-diagonals-data.ts. The units are close
// to one pixel each when the picture is shown at its natural width; y goes
// down the page.

export type PairPoint = { x: number; y: number };
export type PairLine = { from: PairPoint; to: PairPoint };
// A label's box, centred on (x, y).
export type PairLabel = PairPoint & {
  text: string;
  width: number;
  height: number;
};

export const PAIR_LABEL_SIZE = 14;
export const NOTE_SIZE = 12;
export const LABEL_HEIGHT = 15;
export const RADIANS = Math.PI / 180;
// Between a line and its label: room for a mark across the line.
export const GAP = 9;
const TICK = 5;
const TICK_SPACE = 4;
export const ARC = 14;
export const ARC_SPACE = 4;
export const SQUARE = 11;
// Where along its line a label is tried, the middle first.
export const PLACES = [0.5, 0.4, 0.6, 0.3, 0.7, 0.22, 0.78];

// Roughly how wide a label is drawn, to keep it off the lines.
export function labelWidth(text: string): number {
  const signs = text.split('°').length - 1;
  return (text.length - signs) * 8.6 + signs * 6;
}

export function box(at: PairPoint, text: string): PairLabel {
  return { ...at, text, width: labelWidth(text), height: LABEL_HEIGHT };
}

export function unit(from: PairPoint, to: PairPoint): PairPoint {
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  return { x: (to.x - from.x) / length, y: (to.y - from.y) / length };
}

export function along(
  from: PairPoint,
  way: PairPoint,
  reach: number,
): PairPoint {
  return { x: from.x + way.x * reach, y: from.y + way.y * reach };
}

export function between(line: PairLine, part: number): PairPoint {
  return {
    x: line.from.x + (line.to.x - line.from.x) * part,
    y: line.from.y + (line.to.y - line.from.y) * part,
  };
}

export function distance(a: PairPoint, b: PairPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// The angle in degrees at `corner` between the lines to `a` and to `b`.
export function angleAt(corner: PairPoint, a: PairPoint, b: PairPoint): number {
  const u = unit(corner, a);
  const v = unit(corner, b);
  const dot = Math.max(-1, Math.min(1, u.x * v.x + u.y * v.y));
  return Math.acos(dot) / RADIANS;
}

export function sidesOf(corners: PairPoint[]): PairLine[] {
  return corners.map((corner, index) => ({
    from: corner,
    to: corners[(index + 1) % corners.length] ?? corner,
  }));
}

// +1 when the corners go clockwise on the screen, -1 when the other way, 0
// when the outline is not a convex shape.
export function turning(corners: PairPoint[]): number {
  let sign = 0;
  const count = corners.length;
  for (let index = 0; index < count; index += 1) {
    const a = corners[index] ?? { x: 0, y: 0 };
    const b = corners[(index + 1) % count] ?? a;
    const c = corners[(index + 2) % count] ?? a;
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) < 1e-9) return 0;
    const turn = Math.sign(cross);
    if (sign !== 0 && turn !== sign) return 0;
    sign = turn;
  }
  return sign;
}

// The way out of a convex shape from one of its sides.
export function outwardOf(line: PairLine, clockwise: boolean): PairPoint {
  const way = unit(line.from, line.to);
  return clockwise ? { x: way.y, y: -way.x } : { x: -way.y, y: way.x };
}

// True when the line passes through the label's box.
export function crosses(
  box: PairLabel,
  line: PairLine,
  margin: number,
): boolean {
  const halfWidth = box.width / 2 + margin;
  const halfHeight = box.height / 2 + margin;
  const dx = line.to.x - line.from.x;
  const dy = line.to.y - line.from.y;
  let enter = 0;
  let leave = 1;
  for (const [step, room] of [
    [-dx, line.from.x - (box.x - halfWidth)],
    [dx, box.x + halfWidth - line.from.x],
    [-dy, line.from.y - (box.y - halfHeight)],
    [dy, box.y + halfHeight - line.from.y],
  ] as const) {
    if (step === 0) {
      if (room < 0) return false;
    } else if (step < 0) enter = Math.max(enter, room / step);
    else leave = Math.min(leave, room / step);
  }
  return enter <= leave;
}

// True when two boxes are closer than `apart`.
export function overlaps(a: PairLabel, b: PairLabel, apart: number): boolean {
  return (
    Math.abs(a.x - b.x) < (a.width + b.width) / 2 + apart &&
    Math.abs(a.y - b.y) < (a.height + b.height) / 2 + apart
  );
}

// The first spot that keeps clear of every line and every box, or the first
// spot when none does.
export function firstClear(
  spots: PairLabel[],
  lines: PairLine[],
  boxes: PairLabel[],
): PairLabel | null {
  const clear = spots.find(
    (spot) =>
      !lines.some((line) => crosses(spot, line, 1.5)) &&
      !boxes.some((other) => overlaps(spot, other, 3)),
  );
  return clear ?? spots[0] ?? null;
}

// How far a point is from a line.
export function distanceToLine(point: PairPoint, line: PairLine): number {
  const dx = line.to.x - line.from.x;
  const dy = line.to.y - line.from.y;
  const length = dx * dx + dy * dy || 1;
  const part = Math.max(
    0,
    Math.min(
      1,
      ((point.x - line.from.x) * dx + (point.y - line.from.y) * dy) / length,
    ),
  );
  return distance(point, {
    x: line.from.x + dx * part,
    y: line.from.y + dy * part,
  });
}

// Of the spots that keep clear, the one furthest from the lines in `away`
// (so that a label inside a shape is not read as a side's label); the first
// spot when none keeps clear.
export function clearestAway(
  spots: PairLabel[],
  lines: PairLine[],
  boxes: PairLabel[],
  away: PairLine[],
): PairLabel | null {
  const clear = spots.filter(
    (spot) =>
      !lines.some((line) => crosses(spot, line, 1.5)) &&
      !boxes.some((other) => overlaps(spot, other, 3)),
  );
  let best: PairLabel | null = null;
  let room = -Infinity;
  for (const spot of clear) {
    const near = Math.min(...away.map((line) => distanceToLine(spot, line)));
    // A little more room is not worth moving far along the line.
    if (near > room + 4) {
      best = spot;
      room = near;
    }
  }
  return best ?? spots[0] ?? null;
}

// Short strokes across the middle of a line, one for each mark.
export function ticksOn(line: PairLine, count: number): PairLine[] {
  const way = unit(line.from, line.to);
  const across = { x: -way.y, y: way.x };
  const middle = between(line, 0.5);
  return Array.from({ length: count }, (_, index) => {
    const centre = along(middle, way, (index - (count - 1) / 2) * TICK_SPACE);
    return {
      from: along(centre, across, -TICK),
      to: along(centre, across, TICK),
    };
  });
}

// One arc in a corner, from one side round to the other.
export type PairArc = {
  from: PairPoint;
  to: PairPoint;
  radius: number;
  // 1 when the arc turns clockwise on the screen.
  sweep: 0 | 1;
};

// The arcs in a corner, smallest first.
export function arcsAt(
  corner: PairPoint,
  a: PairPoint,
  b: PairPoint,
  count: number,
): PairArc[] {
  const u = unit(corner, a);
  const v = unit(corner, b);
  const sweep = u.x * v.y - u.y * v.x > 0 ? 1 : 0;
  return Array.from({ length: count }, (_, index) => {
    const radius = ARC + index * ARC_SPACE;
    return {
      from: along(corner, u, radius),
      to: along(corner, v, radius),
      radius,
      sweep,
    };
  });
}

const fixed = (value: number) => value.toFixed(2);
const at = (point: PairPoint) => `${fixed(point.x)} ${fixed(point.y)}`;

// The `d` of an SVG path through the points.
export function pathThrough(points: PairPoint[], closed: boolean): string {
  const path = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${at(point)}`)
    .join(' ');
  return closed ? `${path} Z` : path;
}

export function arcPath(arc: PairArc): string {
  return `M ${at(arc.from)} A ${fixed(arc.radius)} ${fixed(arc.radius)} 0 0 ${String(arc.sweep)} ${at(arc.to)}`;
}

// A small square in a corner whose lines meet at a right angle.
export function squareAt(
  corner: PairPoint,
  a: PairPoint,
  b: PairPoint,
): PairPoint[] {
  const u = unit(corner, a);
  const v = unit(corner, b);
  return [
    along(corner, u, SQUARE),
    {
      x: corner.x + (u.x + v.x) * SQUARE,
      y: corner.y + (u.y + v.y) * SQUARE,
    },
    along(corner, v, SQUARE),
  ];
}

// A label inside a corner, on the line halfway between its two sides, far
// enough out to clear the arcs and both sides.
export function cornerLabel(
  corner: PairPoint,
  a: PairPoint,
  b: PairPoint,
  text: string,
  arcs: number,
  square: boolean,
): PairLabel {
  const u = unit(corner, a);
  const v = unit(corner, b);
  const middle = unit({ x: 0, y: 0 }, { x: u.x + v.x, y: u.y + v.y });
  const half = (angleAt(corner, a, b) / 2) * RADIANS;
  const label = box(corner, text);
  const round = Math.hypot(label.width, label.height) / 2;
  const marks =
    arcs > 0 ? ARC + (arcs - 1) * ARC_SPACE : square ? SQUARE * 1.5 : 0;
  const reach = Math.max(marks + 3 + round, (round + 2) / Math.sin(half));
  return { ...label, ...along(corner, middle, reach) };
}

// A corner's letter, outside the shape on the line halfway between its two
// sides.
export function letterOutside(
  corner: PairPoint,
  a: PairPoint,
  b: PairPoint,
  text: string,
): PairLabel {
  const u = unit(corner, a);
  const v = unit(corner, b);
  const sum = { x: u.x + v.x, y: u.y + v.y };
  const way =
    Math.hypot(sum.x, sum.y) < 1e-6
      ? { x: -u.y, y: u.x }
      : unit({ x: 0, y: 0 }, { x: -sum.x, y: -sum.y });
  const label = box(corner, text);
  const reach =
    6 + (Math.abs(way.x) * label.width + Math.abs(way.y) * label.height) / 2;
  return { ...label, ...along(corner, way, reach) };
}

// Spots for a label beside a line, on the side `outward` points to.
export function spotsBeside(
  line: PairLine,
  outward: PairPoint,
  text: string,
  parts: number[],
): PairLabel[] {
  const width = labelWidth(text);
  const reach =
    (Math.abs(outward.x) * width + Math.abs(outward.y) * LABEL_HEIGHT) / 2;
  return parts.map((part) => ({
    ...along(between(line, part), outward, GAP + reach),
    text,
    width,
    height: LABEL_HEIGHT,
  }));
}

export type Bounds = { x: number; y: number; width: number; height: number };

export function boundsOf(points: PairPoint[], labels: PairLabel[]): Bounds {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  for (const label of labels) {
    xs.push(label.x - label.width / 2, label.x + label.width / 2);
    ys.push(label.y - label.height / 2, label.y + label.height / 2);
  }
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

// The values, each within its range, that make `cost` smallest: a grid over
// the ranges, then smaller and smaller steps from the best point found.
// With nothing to fit, the starting values win.
export type FitRange = { low: number; high: number; start: number };

export function bestFit(
  ranges: FitRange[],
  cost: (values: number[]) => number,
): number[] {
  const starts = ranges.map((range) => range.start);
  if (ranges.length === 0) return starts;
  const spans = ranges.map((range) => range.high - range.low);
  const total = (values: number[]) => {
    const raw = cost(values);
    if (!Number.isFinite(raw)) return Infinity;
    let pull = 0;
    for (const [index, value] of values.entries())
      pull += ((value - (starts[index] ?? 0)) / (spans[index] ?? 1)) ** 2;
    return raw + 1e-4 * pull;
  };
  const counts = ranges.length <= 2 ? 17 : ranges.length === 3 ? 10 : 7;
  let best = starts;
  let bestCost = total(starts);
  const values = [...starts];
  const walk = (depth: number) => {
    const range = ranges[depth];
    if (!range) {
      const value = total(values);
      if (value < bestCost) {
        bestCost = value;
        best = [...values];
      }
      return;
    }
    for (let step = 0; step < counts; step += 1) {
      values[depth] =
        range.low + ((range.high - range.low) * step) / (counts - 1);
      walk(depth + 1);
    }
  };
  walk(0);
  let steps = spans.map((span) => span / (counts - 1));
  for (let round = 0; round < 80; round += 1) {
    let moved = false;
    for (const [index, range] of ranges.entries())
      for (const sign of [-1, 1]) {
        const next = [...best];
        const value = (best[index] ?? 0) + sign * (steps[index] ?? 0);
        next[index] = Math.min(range.high, Math.max(range.low, value));
        const nextCost = total(next);
        if (nextCost < bestCost - 1e-12) {
          best = next;
          bestCost = nextCost;
          moved = true;
        }
      }
    if (!moved) steps = steps.map((step) => step / 2);
    if (steps.every((step, index) => step / (spans[index] ?? 1) < 1e-4)) break;
  }
  return best;
}

// How far the drawing is from the numbers written on it.
export type Check = {
  // Lengths: [index of part, written size], all in one unit.
  lengths: [number, number][];
  // Angles: [index of angle, written size in degrees].
  degrees: [number, number][];
  // Angles marked as right angles.
  rights: number[];
  // Groups of parts that carry the same number of marks.
  equalParts: number[][];
  // Groups of angles that carry the same number of arcs.
  equalAngles: number[][];
};

export function groupsOf(counts: number[]): number[][] {
  const groups: number[][] = [];
  for (let count = 1; count <= MAX_MARKS; count += 1) {
    const group = counts.flatMap((value, index) =>
      value === count ? [index] : [],
    );
    if (group.length > 1) groups.push(group);
  }
  return groups;
}

// The cost of a drawing whose parts measure `parts` and whose angles
// measure `angles` (degrees).
export function checkCost(check: Check, parts: number[], angles: number[]) {
  let cost = 0;
  const part = (index: number) => parts[index] ?? 1;
  const angle = (index: number) => angles[index] ?? 90;
  for (const [i, [a, va]] of check.lengths.entries())
    for (const [b, vb] of check.lengths.slice(i + 1))
      cost += (Math.log(part(a) / part(b)) - Math.log(va / vb)) ** 2;
  for (const [index, size] of check.degrees)
    cost += ((angle(index) - size) * RADIANS) ** 2;
  for (const index of check.rights)
    cost += 50 * ((angle(index) - 90) * RADIANS) ** 2;
  for (const group of check.equalParts)
    for (const other of group.slice(1))
      cost += 20 * Math.log(part(other) / part(group[0] ?? other)) ** 2;
  for (const group of check.equalAngles)
    for (const other of group.slice(1))
      cost += 20 * ((angle(other) - angle(group[0] ?? other)) * RADIANS) ** 2;
  return cost;
}

// 'bad' when the drawing badly contradicts its numbers or marks, 'untrue'
// when it is close but not true to size, 'true' otherwise.
export type Verdict = 'bad' | 'untrue' | 'true';

export function judge(
  check: Check,
  parts: number[],
  angles: number[],
): Verdict {
  const part = (index: number) => parts[index] ?? 1;
  const angle = (index: number) => angles[index] ?? 90;
  for (const index of check.rights)
    if (Math.abs(angle(index) - 90) > 1.5) return 'bad';
  for (const group of check.equalParts)
    for (const other of group.slice(1))
      if (Math.abs(Math.log(part(other) / part(group[0] ?? other))) > 0.05)
        return 'bad';
  for (const group of check.equalAngles)
    for (const other of group.slice(1))
      if (Math.abs(angle(other) - angle(group[0] ?? other)) > 2.5) return 'bad';
  let verdict: Verdict = 'true';
  for (const [index, size] of check.degrees) {
    const off = Math.abs(angle(index) - size);
    if (off > 20) return 'bad';
    if (off > 2) verdict = 'untrue';
  }
  for (const [i, [a, va]] of check.lengths.entries())
    for (const [b, vb] of check.lengths.slice(i + 1)) {
      // A part written much longer is never drawn shorter.
      if (va > 1.1 * vb && part(a) <= part(b)) return 'bad';
      if (vb > 1.1 * va && part(b) <= part(a)) return 'bad';
      if (Math.abs(Math.log(part(a) / part(b)) - Math.log(va / vb)) > 0.04)
        verdict = 'untrue';
    }
  return verdict;
}

// The written lengths of some parts, all in one unit; null when two units
// are mixed.
export function writtenLengths(
  labels: string[],
): { lengths: [number, number][]; unit: string } | null {
  const units = new Set<string>();
  const lengths: [number, number][] = [];
  for (const [index, label] of labels.entries()) {
    const length = lengthOf(label);
    if (!length) continue;
    units.add(length.unit);
    lengths.push([index, length.size]);
  }
  if (units.size > 1) return null;
  return { lengths, unit: [...units][0] ?? '' };
}

export function writtenDegrees(labels: string[]): [number, number][] {
  return labels.flatMap((label, index) => {
    const degrees = degreesOf(label);
    return degrees === null ? [] : [[index, degrees] as [number, number]];
  });
}

// ---------------------------------------------------------------------------
// The shape pair.

export type PairShape = {
  // The corner letters going round the shape, or all "" for none. A
  // triangle's first three corners are drawn bottom left, bottom right, top
  // (or bottom left, top, bottom right when `clockwise`); a shape with 4
  // sides starts at the bottom left the same way.
  corners: string[];
  // A short name written under the shape ("A", "B"), or "".
  name: string;
  // 'rectangle' draws a 4-sided shape with square corners. Never spoken.
  outline: 'any' | 'rectangle';
  // One entry for each side; side 0 runs from corner 0 to corner 1.
  sides: string[];
  sideMarks: number[];
  // One entry for each corner.
  angles: string[];
  arcs: number[];
  rightAngles: boolean[];
  clockwise: boolean;
};

export type ShapePairSpec = {
  type: 'shape_pair';
  shapes: [PairShape, PairShape];
  // The second shape only: for each of its corners, the index of the first
  // shape's corner it is a copy of; null when it is drawn from its own
  // numbers.
  copy: number[] | null;
  // The second shape only: mirrored left to right, then turned clockwise by
  // this many degrees.
  flip: boolean;
  turn: number;
  // Shown and spoken when set, and set by the reader whenever the numbers
  // cannot all be drawn true.
  notToScale: boolean;
};

const MAX_NAME = 10;
const NAME = /^[A-Za-z0-9 ]+$/;

type RawSecond = {
  copy: unknown;
  turn: unknown;
  bottom: unknown;
  flip: unknown;
};

function readPairShape(
  value: unknown,
): (PairShape & { raw: RawSecond }) | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return null;
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.corners)) return null;
  const count = record.corners.length;
  if (count !== 3 && count !== 4) return null;
  const corners: string[] = [];
  for (const entry of record.corners as unknown[]) {
    if (typeof entry !== 'string') return null;
    const text = entry.trim();
    if (text !== '' && !CORNER_LETTER.test(text)) return null;
    corners.push(text);
  }
  const lettered = corners.every((letter) => letter !== '');
  if (!lettered && corners.some((letter) => letter !== '')) return null;
  const name = record.name === undefined ? '' : record.name;
  if (
    typeof name !== 'string' ||
    name.trim().length > MAX_NAME ||
    (name.trim() !== '' && !NAME.test(name.trim()))
  )
    return null;
  const outline = record.outline === undefined ? 'any' : record.outline;
  if (outline !== 'any' && outline !== 'rectangle') return null;
  if (outline === 'rectangle' && count !== 4) return null;
  const clockwise = record.clockwise === undefined ? false : record.clockwise;
  if (typeof clockwise !== 'boolean') return null;
  const sides = readLabelList(record.sides, count, isPartLabel);
  const angles = readLabelList(record.angles, count, isAngleLabel);
  const sideMarks = readCountList(record.sideMarks, count);
  const arcs = readCountList(record.arcs, count);
  const rightAngles = readFlagList(record.rightAngles, count);
  if (!sides || !angles || !sideMarks || !arcs || !rightAngles) return null;
  // Marks, arcs and right angles are read out by the corner letters.
  if (!lettered && [...sideMarks, ...arcs].some((mark) => mark > 0))
    return null;
  if (!lettered && rightAngles.some(Boolean)) return null;
  // A corner has arcs or a square, not both.
  if (rightAngles.some((right, index) => right && (arcs[index] ?? 0) > 0))
    return null;
  // The angle sizes written in a shape add up as they must.
  const degrees = writtenDegrees(angles);
  if (degrees.length === count) {
    const sum = degrees.reduce((total, [, size]) => total + size, 0);
    if (Math.abs(sum - (count - 2) * 180) > 0.5) return null;
  } else if (count === 3) {
    const sum = degrees.reduce((total, [, size]) => total + size, 0);
    if (sum >= 175) return null;
  }
  // Three lengths make a triangle.
  const written = writtenLengths(sides);
  if (!written) return null;
  if (count === 3 && written.lengths.length === 3) {
    const sizes = written.lengths.map(([, size]) => size).sort((a, b) => a - b);
    const [a = 0, b = 0, c = 0] = sizes;
    if (a + b <= c * 1.02) return null;
  }
  return {
    corners,
    name: name.trim(),
    outline,
    sides,
    sideMarks,
    angles,
    arcs,
    rightAngles,
    clockwise,
    raw: {
      copy: record.copy,
      turn: record.turn,
      bottom: record.bottom,
      flip: record.flip,
    },
  };
}

function checkOf(shape: PairShape): Check {
  return {
    lengths: writtenLengths(shape.sides)?.lengths ?? [],
    degrees: writtenDegrees(shape.angles),
    rights: shape.rightAngles.flatMap((right, index) => (right ? [index] : [])),
    equalParts: groupsOf(shape.sideMarks),
    equalAngles: groupsOf(shape.arcs),
  };
}

function measure(points: PairPoint[]): { sides: number[]; angles: number[] } {
  const count = points.length;
  const at = (index: number) =>
    points[((index % count) + count) % count] ?? { x: 0, y: 0 };
  return {
    sides: points.map((point, index) => distance(point, at(index + 1))),
    angles: points.map((point, index) =>
      angleAt(point, at(index - 1), at(index + 1)),
    ),
  };
}

// The outline in its usual place, from its fitted values: corner 0 at the
// bottom left, the bottom side 1 long, going anticlockwise on the screen.
function usualOutline(shape: PairShape, values: number[]): PairPoint[] | null {
  const [a = 0, b = 0, c = 0, d = 0] = values;
  let points: PairPoint[];
  if (shape.corners.length === 3) {
    // a and b: the angles at the two ends of the bottom side.
    if (a + b > 160) return null;
    const slant = Math.sin(b * RADIANS) / Math.sin((a + b) * RADIANS);
    points = [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: slant * Math.cos(a * RADIANS), y: -slant * Math.sin(a * RADIANS) },
    ];
  } else if (shape.outline === 'rectangle') {
    points = [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 1, y: -a },
      { x: 0, y: -a },
    ];
  } else {
    // a and c: the angles at the two ends of the bottom side; b and d: the
    // lengths of the sides from them.
    points = [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 1 - d * Math.cos(c * RADIANS), y: -d * Math.sin(c * RADIANS) },
      { x: b * Math.cos(a * RADIANS), y: -b * Math.sin(a * RADIANS) },
    ];
  }
  if (turning(points) === 0) return null;
  // No corner sharper than 12°: it could not be read on a phone.
  if (measure(points).angles.some((angle) => angle < 12)) return null;
  if (!shape.clockwise) return points;
  const [first = { x: 0, y: 0 }, ...rest] = points;
  return [first, ...rest.reverse()];
}

function fitRanges(shape: PairShape): FitRange[] {
  if (shape.corners.length === 3)
    return [
      { low: 15, high: 145, start: 62 },
      { low: 15, high: 145, start: 50 },
    ];
  if (shape.outline === 'rectangle')
    return [{ low: 0.3, high: 3, start: 0.62 }];
  return [
    { low: 45, high: 135, start: 82 },
    { low: 0.35, high: 1.8, start: 0.78 },
    { low: 45, high: 135, start: 70 },
    { low: 0.35, high: 1.8, start: 0.66 },
  ];
}

// The shape fitted to its own numbers and marks.
function fitShape(shape: PairShape): PairPoint[] | null {
  const check = checkOf(shape);
  const free = shape.corners.length === 4 && shape.outline === 'any';
  const values = bestFit(fitRanges(shape), (trial) => {
    const points = usualOutline(shape, trial);
    if (!points) return Infinity;
    const { sides, angles } = measure(points);
    // Where its lengths leave a 4-sided shape free to lean, it leans as
    // little as they allow: corners far from square look lopsided.
    const lean = free
      ? angles.reduce(
          (total, angle) =>
            total + (Math.max(0, Math.abs(angle - 90) - 25) * RADIANS) ** 2,
          0,
        )
      : 0;
    return checkCost(check, sides, angles) + 1e-3 * lean;
  });
  return usualOutline(shape, values);
}

function verdictOf(shape: PairShape, points: PairPoint[]): Verdict {
  const { sides, angles } = measure(points);
  return judge(checkOf(shape), sides, angles);
}

function worse(a: Verdict, b: Verdict): Verdict {
  if (a === 'bad' || b === 'bad') return 'bad';
  return a === 'untrue' || b === 'untrue' ? 'untrue' : 'true';
}

function rotated(points: PairPoint[], flip: boolean, turn: number) {
  const cos = Math.cos(turn * RADIANS);
  const sin = Math.sin(turn * RADIANS);
  return points.map((point) => {
    const x = flip ? -point.x : point.x;
    return { x: x * cos - point.y * sin, y: x * sin + point.y * cos };
  });
}

function scaled(points: PairPoint[], by: number): PairPoint[] {
  return points.map((point) => ({ x: point.x * by, y: point.y * by }));
}

function perimeter(points: PairPoint[]): number {
  return measure(points).sides.reduce((total, side) => total + side, 0);
}

function sizeOf(points: PairPoint[]): { width: number; height: number } {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  return {
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys),
  };
}

// How much larger, at most, one shape is drawn than the other.
const MOST_LARGER = 1.7;
// The largest a shape is drawn.
const SHAPE_WIDTH = 130;
const SHAPE_HEIGHT = 110;

// The written size for one unit of drawing: the middle of the ratios of
// written lengths to drawn lengths.
function unitsPerDrawn(shape: PairShape, points: PairPoint[]): number | null {
  const lengths = writtenLengths(shape.sides)?.lengths ?? [];
  if (lengths.length === 0) return null;
  const sides = measure(points).sides;
  const ratios = lengths
    .map(([index, size]) => size / (sides[index] ?? 1))
    .sort((a, b) => a - b);
  return ratios[Math.floor(ratios.length / 2)] ?? null;
}

type PairGeometry = {
  first: PairPoint[];
  second: PairPoint[];
  verdict: Verdict;
};

// Both outlines, in picture units, before they are placed side by side.
function pairGeometry(
  first: PairShape,
  second: PairShape,
  copy: number[] | null,
  flip: boolean,
  turn: number,
): PairGeometry | null {
  const fittedFirst = fitShape(first);
  if (!fittedFirst) return null;
  let verdict = verdictOf(first, fittedFirst);
  const firstUnits = writtenLengths(first.sides);
  const secondUnits = writtenLengths(second.sides);
  const firstScale = unitsPerDrawn(first, fittedFirst);
  let shapeTwo: PairPoint[];
  // Sizes of the two shapes can be compared when both carry lengths in one
  // unit.
  let comparable = false;
  let ratio = 1;
  if (copy) {
    shapeTwo = copy.map((index) => fittedFirst[index] ?? { x: 0, y: 0 });
    if (turning(shapeTwo) === 0) return null;
    // The ratio of the second shape's lengths to the first's, the same on
    // every pair of sides with both lengths written.
    const ratios: number[] = [];
    const count = copy.length;
    for (const [index, label] of second.sides.entries()) {
      const mine = lengthOf(label);
      const from = copy[index] ?? 0;
      const to = copy[(index + 1) % count] ?? 0;
      const side = (to - from + count) % count === 1 ? from : to;
      const theirs = lengthOf(first.sides[side] ?? '');
      if (mine && theirs?.unit === mine.unit)
        ratios.push(mine.size / theirs.size);
    }
    if (ratios.length > 0) {
      const [low = 1] = ratios;
      if (ratios.some((value) => Math.abs(Math.log(value / low)) > 0.03))
        return null;
      ratio = low;
      comparable = firstScale !== null;
    }
    verdict = worse(verdict, verdictOf(second, shapeTwo));
  } else {
    const fittedSecond = fitShape(second);
    if (!fittedSecond) return null;
    shapeTwo = fittedSecond;
    verdict = worse(verdict, verdictOf(second, fittedSecond));
    const secondScale = unitsPerDrawn(second, fittedSecond);
    if (
      firstScale !== null &&
      secondScale !== null &&
      firstUnits?.unit === secondUnits?.unit
    ) {
      comparable = true;
      // In the first shape's units.
      shapeTwo = scaled(fittedSecond, secondScale / firstScale);
    } else
      shapeTwo = scaled(
        fittedSecond,
        perimeter(fittedFirst) / perimeter(fittedSecond),
      );
  }
  if (copy) shapeTwo = scaled(shapeTwo, ratio);
  shapeTwo = rotated(shapeTwo, flip, turn);
  // Fit the larger shape to the room for one shape.
  const one = sizeOf(fittedFirst);
  const two = sizeOf(shapeTwo);
  const larger = Math.max(
    one.width / SHAPE_WIDTH,
    one.height / SHAPE_HEIGHT,
    two.width / SHAPE_WIDTH,
    two.height / SHAPE_HEIGHT,
  );
  let firstPicture = scaled(fittedFirst, 1 / larger);
  let secondPicture = scaled(shapeTwo, 1 / larger);
  // Neither is drawn much smaller than the other.
  const span = (points: PairPoint[]) => {
    const size = sizeOf(points);
    return Math.max(size.width / SHAPE_WIDTH, size.height / SHAPE_HEIGHT);
  };
  const across = span(secondPicture) / span(firstPicture);
  if (across > MOST_LARGER) {
    firstPicture = scaled(firstPicture, across / MOST_LARGER);
    if (comparable) verdict = worse(verdict, 'untrue');
  } else if (across < 1 / MOST_LARGER) {
    secondPicture = scaled(secondPicture, 1 / (across * MOST_LARGER));
    if (comparable) verdict = worse(verdict, 'untrue');
  }
  // Marks of the same count on the two shapes are the same size.
  const one2 = measure(firstPicture);
  const two2 = measure(secondPicture);
  const allSides = [...one2.sides, ...two2.sides];
  const allAngles = [...one2.angles, ...two2.angles];
  const verdictAcross = judge(
    {
      lengths: [],
      degrees: [],
      rights: [],
      equalParts: groupsOf([...first.sideMarks, ...second.sideMarks]),
      equalAngles: groupsOf([...first.arcs, ...second.arcs]),
    },
    allSides,
    allAngles,
  );
  verdict = worse(verdict, verdictAcross);
  return { first: firstPicture, second: secondPicture, verdict };
}

// Which of the first shape's corners each of the second shape's corners is
// a copy of: `true` for the same order, or the first shape's letters. The
// corners must still go round the shape in order (either way).
function readCopy(
  value: unknown,
  first: PairShape,
  count: number,
): number[] | null | undefined {
  if (value === undefined || value === false) return null;
  if (first.corners.length !== count) return undefined;
  let copy: number[];
  if (value === true) copy = Array.from({ length: count }, (_, index) => index);
  else {
    if (!Array.isArray(value) || value.length !== count) return undefined;
    copy = [];
    for (const entry of value as unknown[]) {
      if (typeof entry !== 'string') return undefined;
      const index = first.corners.indexOf(entry.trim());
      if (entry.trim() === '' || index < 0) return undefined;
      copy.push(index);
    }
  }
  if (new Set(copy).size !== count) return undefined;
  const steps = copy.map(
    (index, at) => ((copy[(at + 1) % count] ?? 0) - index + count) % count,
  );
  if (
    !steps.every((step) => step === 1) &&
    !steps.every((step) => step === count - 1)
  )
    return undefined;
  return copy;
}

const WHOLE_TURN = 360;

function withoutRaw(shape: PairShape & { raw: RawSecond }): PairShape {
  return {
    corners: shape.corners,
    name: shape.name,
    outline: shape.outline,
    sides: shape.sides,
    sideMarks: shape.sideMarks,
    angles: shape.angles,
    arcs: shape.arcs,
    rightAngles: shape.rightAngles,
    clockwise: shape.clockwise,
  };
}

export function readShapePair(
  record: Record<string, unknown>,
): ShapePairSpec | null {
  const { shapes } = record;
  if (!Array.isArray(shapes) || shapes.length !== 2) return null;
  const first = readPairShape(shapes[0]);
  const second = readPairShape(shapes[1]);
  if (!first || !second) return null;
  if (first.corners.length !== second.corners.length) return null;
  // The first shape is never turned or a copy.
  const { raw } = first;
  if (
    raw.copy !== undefined ||
    raw.turn !== undefined ||
    raw.bottom !== undefined ||
    raw.flip !== undefined
  )
    return null;
  // No two corners share a letter.
  const letters = [...first.corners, ...second.corners].filter(
    (letter) => letter !== '',
  );
  if (new Set(letters).size !== letters.length) return null;
  const notToScale =
    record.notToScale === undefined ? false : record.notToScale;
  if (typeof notToScale !== 'boolean') return null;
  const count = second.corners.length;
  const copy = readCopy(second.raw.copy, first, count);
  if (copy === undefined) return null;
  const flip = second.raw.flip === undefined ? false : second.raw.flip;
  if (typeof flip !== 'boolean') return null;
  // Turned and flipped shapes are read out by their corner letters.
  const lettered = second.corners.every((letter) => letter !== '');
  const { turn, bottom } = second.raw;
  if (turn !== undefined && bottom !== undefined) return null;
  if (
    !lettered &&
    (flip || (turn !== undefined && turn !== 0) || bottom !== undefined)
  )
    return null;
  let angle = 0;
  if (turn !== undefined) {
    if (
      typeof turn !== 'number' ||
      !Number.isInteger(turn) ||
      turn <= -180 ||
      turn > 180
    )
      return null;
    angle = turn;
  }
  const firstShape = withoutRaw(first);
  const secondShape = withoutRaw(second);
  if (bottom !== undefined) {
    // Turned so that this side lies flat along the bottom, its first letter
    // on the left, and the rest of the shape above it.
    const resolved = turnForBottom(bottom, firstShape, secondShape, copy, flip);
    if (resolved === null) return null;
    angle = resolved;
  }
  const geometry = pairGeometry(firstShape, secondShape, copy, flip, angle);
  if (!geometry || geometry.verdict === 'bad') return null;
  return {
    type: 'shape_pair',
    shapes: [firstShape, secondShape],
    copy,
    flip,
    turn: angle,
    notToScale: notToScale || geometry.verdict === 'untrue',
  };
}

function turnForBottom(
  value: unknown,
  first: PairShape,
  second: PairShape,
  copy: number[] | null,
  flip: boolean,
): number | null {
  if (typeof value !== 'string') return null;
  const [left = '', right = '', ...more] = value.trim().split('');
  if (more.length > 0) return null;
  const from = second.corners.indexOf(left);
  const to = second.corners.indexOf(right);
  const count = second.corners.length;
  if (from < 0 || to < 0) return null;
  if ((to - from + count) % count !== 1 && (from - to + count) % count !== 1)
    return null;
  const geometry = pairGeometry(first, second, copy, flip, 0);
  if (!geometry) return null;
  const a = geometry.second[from];
  const b = geometry.second[to];
  if (!a || !b) return null;
  const raw = -Math.atan2(b.y - a.y, b.x - a.x) / RADIANS;
  // Whole degrees, between -180 and 180.
  let angle = Math.round(raw);
  if (angle <= -180) angle += WHOLE_TURN;
  if (angle > 180) angle -= WHOLE_TURN;
  const turned = rotated(geometry.second, false, angle);
  const base = turned[from]?.y ?? 0;
  // The rest of the shape is above the side (y goes down).
  if (turned.some((point) => point.y > base + 0.5)) return null;
  return angle;
}

// ---------------------------------------------------------------------------
// What a screen reader says.

function noun(shape: PairShape): string {
  return shape.corners.length === 3 ? 'triangle' : 'shape';
}

function shapeTitle(shape: PairShape, where: 'left' | 'right'): string {
  if (shape.corners.every((letter) => letter !== ''))
    return shape.corners.join('');
  return shape.name !== '' ? shape.name : `one on the ${where}`;
}

function turnedWords(spec: ShapePairSpec): string {
  const turned = Math.abs(spec.turn) > 0;
  if (spec.flip && turned) return ', drawn flipped and turned';
  if (spec.flip) return ', drawn flipped';
  return turned ? ', drawn turned' : '';
}

function shapeWords(shape: PairShape, where: 'left' | 'right'): string[] {
  const words: string[] = [];
  const count = shape.corners.length;
  if (shape.corners.every((letter) => letter !== '')) {
    for (const [index, label] of shape.sides.entries()) {
      const name = `${shape.corners[index] ?? ''}${shape.corners[(index + 1) % count] ?? ''}`;
      const parts: string[] = [];
      const marks = shape.sideMarks[index] ?? 0;
      if (marks > 0) parts.push(`has ${marksWords(marks, 'small mark')}`);
      if (label !== '') parts.push(labelWords(label));
      if (parts.length > 0) words.push(`Side ${name} ${parts.join(' and ')}.`);
    }
    for (const [index, label] of shape.angles.entries()) {
      const name = shape.corners[index] ?? '';
      const parts: string[] = [];
      const arcs = shape.arcs[index] ?? 0;
      if (arcs > 0) parts.push(`has ${marksWords(arcs, 'arc')}`);
      if (shape.rightAngles[index]) parts.push('is marked as a right angle');
      if (label !== '') parts.push(labelWords(label));
      if (parts.length > 0) words.push(`Angle ${name} ${parts.join(' and ')}.`);
    }
    return words;
  }
  const who =
    shape.name !== ''
      ? `${noun(shape) === 'triangle' ? 'Triangle' : 'Shape'} ${shape.name}`
      : `The ${noun(shape)} on the ${where}`;
  if (shape.sides.some((label) => label !== ''))
    words.push(
      `${who} has its sides labelled, going round from the bottom side: ${spokenList(shape.sides, 'side')}.`,
    );
  if (shape.angles.some((label) => label !== ''))
    words.push(
      `${who} has its corners labelled, going round from the bottom left corner: ${spokenList(shape.angles, 'corner')}.`,
    );
  return words;
}

export function describeShapePair(spec: ShapePairSpec): string {
  const [first, second] = spec.shapes;
  const kind =
    first.corners.length === 3
      ? 'Two triangles'
      : 'Two shapes with 4 straight sides';
  const lettered = (shape: PairShape) =>
    shape.corners.every((letter) => letter !== '') || shape.name !== '';
  const opening =
    lettered(first) || lettered(second)
      ? `${kind}: ${shapeTitle(first, 'left')} on the left and ${shapeTitle(second, 'right')} on the right${turnedWords(spec)}.`
      : `${kind}, one on the left and one on the right${turnedWords(spec)}.`;
  const parts = [
    opening,
    ...shapeWords(first, 'left'),
    ...shapeWords(second, 'right'),
  ];
  if (spec.notToScale) parts.push('Not drawn to scale.');
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// Where everything is drawn.

export type PairShapePicture = {
  outline: PairPoint[];
  ticks: PairLine[];
  arcs: PairArc[];
  squares: PairPoint[][];
  letters: PairLabel[];
  sideLabels: PairLabel[];
  angleLabels: PairLabel[];
  name: PairLabel | null;
};

export type ShapePairPicture = {
  shapes: PairShapePicture[];
  note: PairLabel | null;
  box: Bounds;
};

const BETWEEN_SHAPES = 40;
const PAD = 6;
export const NOT_TO_SCALE = 'not drawn to scale';

function drawShape(points: PairPoint[], shape: PairShape): PairShapePicture {
  const count = points.length;
  const at = (index: number) =>
    points[((index % count) + count) % count] ?? { x: 0, y: 0 };
  const lines = sidesOf(points);
  const clockwise = turning(points) > 0;
  const ticks = lines.flatMap((line, index) =>
    ticksOn(line, shape.sideMarks[index] ?? 0),
  );
  const arcs: PairArc[] = [];
  const squares: PairPoint[][] = [];
  const letters: PairLabel[] = [];
  const angleLabels: PairLabel[] = [];
  for (const [index, corner] of points.entries()) {
    const before = at(index - 1);
    const after = at(index + 1);
    const letter = shape.corners[index] ?? '';
    if (letter !== '')
      letters.push(letterOutside(corner, before, after, letter));
    const arcCount = shape.arcs[index] ?? 0;
    arcs.push(...arcsAt(corner, before, after, arcCount));
    const right = shape.rightAngles[index] ?? false;
    if (right) squares.push(squareAt(corner, before, after));
    const label = shape.angles[index] ?? '';
    if (label !== '')
      angleLabels.push(
        cornerLabel(corner, before, after, label, arcCount, right),
      );
  }
  const sideLabels: PairLabel[] = [];
  for (const [index, line] of lines.entries()) {
    const text = shape.sides[index] ?? '';
    if (text === '') continue;
    const spots = spotsBeside(line, outwardOf(line, clockwise), text, PLACES);
    const spot = firstClear(
      spots,
      [...lines, ...ticks],
      [...letters, ...angleLabels, ...sideLabels],
    );
    if (spot) sideLabels.push(spot);
  }
  return {
    outline: points,
    ticks,
    arcs,
    squares,
    letters,
    sideLabels,
    angleLabels,
    name: null,
  };
}

function moved(picture: PairShapePicture, dx: number, dy: number) {
  const shift = (point: PairPoint) => ({ x: point.x + dx, y: point.y + dy });
  const shiftLabel = (label: PairLabel) => ({ ...label, ...shift(label) });
  return {
    ...picture,
    outline: picture.outline.map(shift),
    ticks: picture.ticks.map((tick) => ({
      from: shift(tick.from),
      to: shift(tick.to),
    })),
    arcs: picture.arcs.map((arc) => ({
      ...arc,
      from: shift(arc.from),
      to: shift(arc.to),
    })),
    squares: picture.squares.map((square) => square.map(shift)),
    letters: picture.letters.map(shiftLabel),
    sideLabels: picture.sideLabels.map(shiftLabel),
    angleLabels: picture.angleLabels.map(shiftLabel),
  };
}

function labelsOf(picture: PairShapePicture): PairLabel[] {
  return [...picture.letters, ...picture.sideLabels, ...picture.angleLabels];
}

const pictures = new WeakMap<ShapePairSpec, ShapePairPicture>();

export function shapePairPicture(spec: ShapePairSpec): ShapePairPicture {
  const known = pictures.get(spec);
  if (known) return known;
  const [first, second] = spec.shapes;
  const geometry = pairGeometry(first, second, spec.copy, spec.flip, spec.turn);
  // The reader refuses a pair it cannot draw, so this is only a fallback.
  const one = drawShape(geometry?.first ?? [], first);
  const two = drawShape(geometry?.second ?? [], second);
  const boundsOne = boundsOf(one.outline, labelsOf(one));
  const boundsTwo = boundsOf(two.outline, labelsOf(two));
  // Side by side, the middles of the two outlines level.
  const middle = (points: PairPoint[]) => {
    const ys = points.map((point) => point.y);
    return (Math.min(...ys) + Math.max(...ys)) / 2;
  };
  const left = moved(one, -boundsOne.x, -middle(one.outline));
  const right = moved(
    two,
    boundsOne.width + BETWEEN_SHAPES - boundsTwo.x,
    -middle(two.outline),
  );
  const shapes = [left, right];
  let bottom = Math.max(
    ...shapes
      .map((shape) => boundsOf(shape.outline, labelsOf(shape)))
      .map((bounds) => bounds.y + bounds.height),
  );
  const names: PairLabel[] = [];
  for (const [index, shape] of shapes.entries()) {
    const text = spec.shapes[index]?.name ?? '';
    if (text === '') continue;
    const xs = shape.outline.map((point) => point.x);
    const name = box(
      {
        x: (Math.min(...xs) + Math.max(...xs)) / 2,
        y: bottom + 6 + LABEL_HEIGHT / 2,
      },
      text,
    );
    shape.name = name;
    names.push(name);
  }
  if (names.length > 0) bottom += 6 + LABEL_HEIGHT;
  const all = [...labelsOf(left), ...labelsOf(right), ...names];
  const points = [...left.outline, ...right.outline];
  const width = boundsOf(points, all);
  const note = spec.notToScale
    ? {
        x: width.x + width.width / 2,
        y: bottom + 8 + LABEL_HEIGHT / 2,
        text: NOT_TO_SCALE,
        width: NOT_TO_SCALE.length * 6.4,
        height: LABEL_HEIGHT,
      }
    : null;
  const bounds = boundsOf(points, note ? [...all, note] : all);
  const picture = {
    shapes,
    note,
    box: {
      x: bounds.x - PAD,
      y: bounds.y - PAD,
      width: bounds.width + 2 * PAD,
      height: bounds.height + 2 * PAD,
    },
  };
  pictures.set(spec, picture);
  return picture;
}
